import { colors } from '@ardoq/design-tokens';
import type { LabelIcon } from './props';

const SIZE = 15;
const ICON_SIZE = 17;

export const createNodeSVG = (
  borderColor = colors.brand200,
  fillColor = colors.brand50
) => {
  const group = document.createElementNS('http://www.w3.org/2000/svg', 'g');

  const ellipse = document.createElementNS(
    'http://www.w3.org/2000/svg',
    'ellipse'
  );
  ellipse.setAttribute('rx', `${SIZE}`);
  ellipse.setAttribute('ry', `${SIZE}`);
  ellipse.setAttribute('cx', `${SIZE}`);
  ellipse.setAttribute('cy', `${SIZE}`);
  ellipse.setAttribute('fill', fillColor);
  ellipse.setAttribute('stroke', borderColor);
  ellipse.setAttribute('stroke-width', '0.5');
  group.appendChild(ellipse);

  return group;
};

export const addSVGIcon = (originalGroup: SVGElement, icon: SVGElement) => {
  icon.setAttribute('width', `${ICON_SIZE}`);
  icon.setAttribute('height', `${ICON_SIZE}`);
  icon.setAttribute('x', `${SIZE - ICON_SIZE / 2}`);
  icon.setAttribute('y', `${SIZE - ICON_SIZE / 2}`);

  return includeIconInGroup(originalGroup, icon);
};

export const addSVGText = (originalGroup: SVGElement, text: SVGTextElement) => {
  text.setAttribute('x', `${SIZE}`);
  text.setAttribute('y', `${SIZE}`);
  text.style.fontSize = `${SIZE}px`;
  text.style.textAnchor = 'middle';
  text.style.dominantBaseline = 'central';

  return includeIconInGroup(originalGroup, text);
};

const includeIconInGroup = (group: SVGElement, element: SVGElement) => {
  // Make it easy to style and transform the icon with CSS, for instance
  // for hover effects
  element.setAttribute('class', 'node-icon');
  element.style.transformOrigin = `${SIZE}px ${SIZE}px`;

  group.appendChild(element);
  return group;
};

export const translateNodeSVG = (group: SVGElement, x: number, y: number) => {
  const duplicate = group.cloneNode(true) as SVGElement;
  duplicate.setAttribute('transform', `translate(${x} ${y})`);

  return duplicate;
};

export const createSVGElement = <K extends keyof SVGElementTagNameMap>(
  tag: K
): SVGElementTagNameMap[K] =>
  document.createElementNS('http://www.w3.org/2000/svg', tag);

export const LABEL_ICON_SIZE = 16;
export const LABEL_ICON_GAP = 4;

type LabelIconSVGArgs = {
  icon: LabelIcon;
  width: number;
  height: number;
};

export const createLabelIconSVG = ({
  icon,
  width,
  height,
}: LabelIconSVGArgs): SVGElement => {
  if (icon.type === 'image') {
    const image = createSVGElement('image');
    image.setAttribute('href', icon.imageUrl);
    image.setAttribute('width', `${width}`);
    image.setAttribute('height', `${height}`);
    image.setAttribute('aria-hidden', 'true');
    return image;
  }

  if (icon.type === 'font-awesome') {
    const text = createSVGElement('text');
    text.textContent = icon.fontAwesomeChar;
    text.style.fontFamily = 'FontAwesome';
    text.style.fill = icon.color;
    text.setAttribute('font-size', `${height * 0.875}`);
    text.setAttribute('text-anchor', 'middle');
    text.setAttribute('x', `${width / 2}`);
    text.setAttribute('dominant-baseline', 'central');
    text.setAttribute('y', `${height / 2}`);
    text.setAttribute('aria-hidden', 'true');
    return text;
  }

  // SVG sprite
  const use = createSVGElement('use');
  use.setAttribute('href', `#${icon.name}`);
  use.setAttribute('width', `${width}`);
  use.setAttribute('height', `${height}`);
  use.style.color = icon.color;
  return use;
};

type LabelTextSVGArgs = {
  text: string;
  fontFamily: string;
  fontSize: number;
  fontWeight: string;
  fill: string;
  height: number;
};

export const createLabelTextSVG = ({
  text,
  fontFamily,
  fontSize,
  fontWeight,
  fill,
  height,
}: LabelTextSVGArgs): SVGTextElement => {
  const element = createSVGElement('text');
  element.setAttribute('x', `${LABEL_ICON_SIZE + LABEL_ICON_GAP}`);
  element.setAttribute('y', `${height / 2}`);
  element.setAttribute('dominant-baseline', 'central');
  element.setAttribute('font-family', fontFamily);
  element.setAttribute('font-size', `${fontSize}`);
  element.setAttribute('font-weight', fontWeight);
  element.setAttribute('fill', fill);
  element.textContent = text;
  return element;
};
